/* eslint-disable react/jsx-one-expression-per-line */
import React from 'react';
import { makeStyles, MuiThemeProvider } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import classNames from 'classnames';
import LearningCheckQuestions from '../../../micro/learningCheck/LearningCheckQuestions';
import { poslumaTheme, universalStyles } from '../../../../app/theme';
import { IMAGE_PATHS, BASE_URL } from '../../../../constants/paths.constants';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  headerImage: {
    display: 'block',
    width: '100%',
  },
  heading: {
    paddingTop: 30,
    paddingBottom: 20,
  },
  header: {
    paddingTop: 30,
  },
  columnWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    marginLeft: 80,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginBottom: 40,
  },
  text: {
    margin: '0 0 12px 0',
  },
  abbreviations: {
    paddingTop: 40,
    margin: '0 0 12px 0',
    fontSize: 14,
  },
  abbreviationContent: {
    margin: '0 0 12px 0',
    fontSize: 14,
  },
  columnImage: {
    marginLeft: 30,
    paddingBottom: 50,
    height: 700,
  },
  imageHeader: {
    marginBottom: 10,
    fontSize: 22,
    '& > em': {
      display: 'block',
    },
  },
  imageList: {
    paddingLeft: 30,
  },
  superscript: {
    fontSize: 11,
  },
}));

type AcquisitionPage07Props = {
  setNextDisabled: (disabled: boolean) => void;
};

const AcquisitionPage07 = ({ setNextDisabled }: AcquisitionPage07Props): JSX.Element => {
  const classes = useStyles();

  return (
    <MuiThemeProvider theme={poslumaTheme}>
      <div className={classes.poslumaBox}>
        <img
          className={classes.headerImage}
          src={`${BASE_URL}${IMAGE_PATHS.STEPS[2].ACQUISITION_STEP_3.path}`}
          alt={`${IMAGE_PATHS.STEPS[2].ACQUISITION_STEP_3.alt}`}
        />
        <div className={classes.whiteBox}>
          <Typography variant="h2">STEP 3:</Typography>
          <Typography variant="h3">Preparation and Administration of POSLUMA Dose </Typography>
          <Typography variant="h4" className={classes.heading}>
            Prepare the POSLUMA Dose and Administer
          </Typography>
          <div className={classes.columnWrapper}>
            <img
              className={classes.columnImage}
              src={`${BASE_URL}${IMAGE_PATHS.POSLUMA_PREPARE_DOSE_TIMELINE.path}`}
              alt={IMAGE_PATHS.POSLUMA_PREPARE_DOSE_TIMELINE.alt}
            />
            <div className={classes.rightColumn}>
              <div className={classes.row}>
                <Typography variant="h4" className={classes.imageHeader}>
                  Dilute POSLUMA if necessary
                  <sup className={classes.superscript}>1</sup>
                </Typography>
                <Typography variant="body1" className={classes.text}>
                  POSLUMA may be diluted with sterile 0.9% Sodium Chloride Injection, USP
                </Typography>
                <Typography variant="body1" className={classes.text}>
                  The recommended maximum volume of undiluted POSLUMA injection is 5 mL
                </Typography>
                <ul className={classNames(classes.poslumaList, classes.imageList)}>
                  <li>
                    After POSLUMA injection, administer an intravenous flush of sterile 0.9% Sodium Chloride Injection,
                    USP to ensure full delivery of the dose
                  </li>
                </ul>
              </div>
              <div className={classes.row}>
                <Typography variant="h4" className={classes.imageHeader}>
                  Prepare POSLUMA administration
                  <sup className={classes.superscript}>1</sup>
                </Typography>
                <Typography variant="body1" className={classes.text}>
                  Recommended activity for POSLUMA dose is 296 MBq (8 mCi)
                </Typography>
                <Typography variant="body1" className={classes.text}>
                  Inspect the dose visually and discard if any particulate matter or discoloration is present
                </Typography>
                <ul className={classNames(classes.poslumaList, classes.imageList)}>
                  <li>Calculate the necessary volume for administration based on calibration time and required dose</li>
                  <li>Assay the dose in a dose calibrator before administration</li>
                  <li>Dispose of any unused POSLUMA following applicable regulations</li>
                </ul>
                <Typography variant="body1" className={classes.text}>
                  Inject POSLUMA as an intravenous bolus
                </Typography>
              </div>
            </div>
          </div>
          <Typography variant="h4" className={classes.header}>
            Dosimetry
          </Typography>
          <Typography variant="body1" className={classes.text}>
            The effective whole body dose for men is 0.014 mSv/MBq administration
          </Typography>
          <ul className={classNames(classes.poslumaList, classes.imageList)}>
            <li>
              <span>
                4.1 mSv for an 8 mCi (296 MBq)
                <sup className={classes.superscript}>1</sup>
              </span>
            </li>
          </ul>
          <Typography variant="body1" className={classes.text}>
            When PET/CT is performed, the radiation exposure will increase by an amount dependent on the settings used
            in the CT acquisition
            <sup className={classes.superscript}>1</sup>
          </Typography>
          <Typography variant="body1" className={classes.text}>
            For the recommended activity of 296 MBq (8mCi), the highest mean absorbed dose to organs is
            <sup className={classes.superscript}>1</sup>:
          </Typography>
          <ul className={classNames(classes.poslumaList, classes.imageList)}>
            <li>Adrenals, 54.3 mGy (0.184 mGy/MBq)</li>
            <li>Kidneys, 51 mGy (0.172 mGy/MBq)</li>
            <li>Submandibular glands, 43.8 mGy (0.148 mGy/MBq)</li>
          </ul>
          <Typography className={classes.abbreviations}>Abbreviations:</Typography>
          <Typography variant="body1" className={classes.abbreviationContent}>
            USP, United States Pharmacopeia.
          </Typography>
        </div>
      </div>
      <LearningCheckQuestions module="poslumaAcquisition" page={7} setNextDisabled={setNextDisabled} trainingId={2} />
    </MuiThemeProvider>
  );
};

export default AcquisitionPage07;
